import type { StateContext, Store } from '@ngxs/store';
import { Control, type DynamicFlatNode, type IChannel, type ILaneConfig, isSameControlAsId, LaneConfig, type StoreControl } from 'commons';
import { produce } from 'immer';
import { firstValueFrom } from 'rxjs';
import { findbyId } from '../../../../state_utils';
import { db } from '../../../db';
import { LoadTestPhases } from '../../test-phase/test-phase.actions';
import { TreeState } from '../../tree/TreeState';
import { AddLaneConfig, GetBatchChannelData, GetChannelData } from '../actions';
import { GenerateEChartConfig } from '../actions/other.actions';
import type { AddControlToCurrentView } from '../actions/sensortable.actions';
import { getTimeBoundaries } from '../functions/getTimeBoundaries';
import type { MultiViewStateModel } from '../MultiViewStateModel';
import type { SingleView } from '../SingleView';
import { getViewIndex, getViewIndexById, LaneConfigHandler } from '.';

export namespace ControlHandlers {
  /**
   * Add a single control to the current view
   * @param ctx state context
   * @param param1 action parameters
   * @param store NGXS store
   */
  export async function addControl(ctx: StateContext<MultiViewStateModel>, { control: _control, lane, view }: AddControlToCurrentView, store: Store) {
    function hasZoomLevel(state: MultiViewStateModel): boolean {
      const firstZoom = state.views[viewIndex].zoomState[0];
      return firstZoom.start !== 0 || firstZoom.end !== 100;
    }
    // Get the index of either the current view or the view passed in
    const viewIndex = view ? getViewIndexById(ctx, view) : getViewIndex(ctx);
    // convert object to instance
    const control = new Control(_control);

    const node = store.selectSnapshot(TreeState.getFlatTree).find(isSameControlAsId(control.id));
    const channels = (await db.channelInfo.toArray()) as DynamicFlatNode<IChannel>[];

    // Capture state before mutating so we can react after the update
    const wasEmpty = (ctx.getState().views[viewIndex]?.sensorTable?.length ?? 0) === 0;
    const testPhasesVisible = ctx.getState().views[viewIndex]?.testPhasesVisible ?? false;

    // if there is viewIndex found and a node definied
    if (viewIndex >= 0 && node) {
      // prepare a new state object
      const state = produce(ctx.getState(), (draft) => {
        const currentView = draft.views[viewIndex] as SingleView;
        currentView.lanes = currentView.lanes ?? [new LaneConfig().serialize];
        currentView.sensorTable = currentView.sensorTable ?? [];

        if (currentView.lanes.length === 0) {
          ctx.dispatch(new AddLaneConfig(new LaneConfig().serialize));
        }

        const sensorTableControlIndex = currentView.sensorTable?.findIndex(findbyId(control.store));

        if (sensorTableControlIndex < 0) {
          // define the lane index where the control shoulded be added to. Either the lane passed in or the first active lane
          const laneIndex = lane ? currentView.lanes.findIndex(findbyId(lane)) : currentView.lanes.findIndex((l) => l.isActive);

          control.setTime(getTimeBoundaries(ctx, viewIndex, node as DynamicFlatNode<IChannel>));

          currentView.sensorTable = [...currentView.sensorTable, control.store];
          currentView.lanes[laneIndex ?? 0] = {
            ...currentView.lanes[laneIndex ?? 0],
          } as LaneConfig;
          currentView.lanes[laneIndex ?? 0].controls = [...(currentView.lanes[laneIndex ?? 0].controls ?? []), control.store];

          LaneConfigHandler.updateTimestamp(currentView, laneIndex ?? 0, channels);
        }
      });
      // sets the new state
      ctx.setState(state);
      try {
        await db.controls.put(control);
      } catch (e) {
        console.error('Error saving control to IndexedDB', e);
      }
      // figure out if the zoom has been touched
      const _hasZoomLevel = hasZoomLevel(state);

      // Check if context is still valid before dispatching actions
      try {
        // dispatch an action to retrieve data
        ctx
          .dispatch(
            new GetChannelData(
              control.id,
              {},
              {
                replaceDate: _hasZoomLevel,
              }
            )
          )
          .subscribe({
            next: () => {
              try {
                // When a channel is added to a previously empty chart while test
                // phases are visible, reload phases from the API first so the
                // new channel's configuration phases are used, then re-render.
                if (wasEmpty && testPhasesVisible) {
                  firstValueFrom(ctx.dispatch(new LoadTestPhases()))
                    .then(() => {
                      ctx.dispatch(GenerateEChartConfig);
                    })
                    .catch(() => {
                      ctx.dispatch(GenerateEChartConfig);
                    });
                } else {
                  ctx.dispatch(GenerateEChartConfig);
                }
              } catch (err) {
                console.warn('Failed to dispatch GenerateEChartConfig, context may have been destroyed', err);
              }
            },
            error: (err) => console.error('Error in GetChannelData dispatch', err),
          });
      } catch (err) {
        console.warn('Failed to dispatch GetChannelData, context may have been destroyed', err);
      }
    }
  }

  /**
   * Validates controls and returns those with valid nodes
   */
  function validateControls(controls: Control[], store: Store): { control: Control; node: DynamicFlatNode<IChannel> }[] {
    const validControls: {
      control: Control;
      node: DynamicFlatNode<IChannel>;
    }[] = [];

    for (const _control of controls) {
      const control = new Control(_control);
      const node = store.selectSnapshot(TreeState.getFlatTree).find(isSameControlAsId(control.id));

      if (node) {
        validControls.push({
          control,
          node: node as DynamicFlatNode<IChannel>,
        });
      }
    }

    return validControls;
  }

  /**
   * Adds a control to the specified lane in the current view
   */
  function addControlToLane(currentView: SingleView, control: Control, laneIndex: number): void {
    currentView.sensorTable = currentView.sensorTable ?? [];
    currentView.lanes = currentView.lanes ?? [];
    currentView.sensorTable = [...currentView.sensorTable, control.store];
    const targetLaneIndex = laneIndex === -1 ? 0 : laneIndex;
    const targetLane = currentView.lanes[targetLaneIndex];

    if (targetLane) {
      targetLane.controls = [...(targetLane.controls ?? []), control.store];
    }
  }

  /**
   * Add multiple controls to the current view sensor table with batch data retrieval
   * @param ctx state context
   * @param param1 action parameters with multiple controls
   * @param store NGXS store
   */
  export async function addBatchControls(
    ctx: StateContext<MultiViewStateModel>,
    { controls: _controls, lane, view }: { controls: Control[]; lane?: ILaneConfig; view?: string },
    store: Store
  ) {
    // Get the index of either the current view or the view passed in
    const viewIndex = view ? getViewIndexById(ctx, view) : getViewIndex(ctx);

    if (viewIndex < 0) {
      return;
    }

    // Convert objects to Control instances and validate nodes exist
    const validControls = validateControls(_controls, store);

    if (validControls.length === 0) {
      return;
    }

    const channels = (await db.channelInfo.toArray()) as DynamicFlatNode<IChannel>[];

    // Capture state before mutating
    const batchWasEmpty = (ctx.getState().views[viewIndex]?.sensorTable?.length ?? 0) === 0;
    const batchTestPhasesVisible = ctx.getState().views[viewIndex]?.testPhasesVisible ?? false;

    // Update the state with all controls
    const state = produce(ctx.getState(), (draft) => {
      const currentView = draft.views[viewIndex] as SingleView;
      currentView.lanes = currentView.lanes ?? [];
      currentView.sensorTable = currentView.sensorTable ?? [];

      if (currentView.lanes.length === 0) {
        ctx.dispatch(new AddLaneConfig(new LaneConfig().serialize));
      }

      const laneIndex = lane ? currentView.lanes.findIndex(findbyId(lane)) : currentView.lanes.findIndex((l) => l.isActive);

      for (const { control, node } of validControls) {
        const sensorTableControlIndex = currentView.sensorTable?.findIndex(findbyId(control.store));

        if (sensorTableControlIndex < 0) {
          control.setTime(getTimeBoundaries(ctx, viewIndex, node));
          addControlToLane(currentView, control, laneIndex);
        }
      }

      // Update timestamp once for the lane after adding all controls
      LaneConfigHandler.updateTimestamp(currentView, laneIndex === -1 ? 0 : laneIndex, channels);
    });

    // Set the new state
    ctx.setState(state);

    // Save all controls to IndexedDB
    try {
      await Promise.all(validControls.map(({ control }) => db.controls.put(control)));
    } catch (e) {
      console.error('Error saving controls to IndexedDB', e);
    }

    // Check if zoom has been touched
    const firstZoom = state.views[viewIndex].zoomState[0];
    const _hasZoomLevel = firstZoom.start !== 0 || firstZoom.end !== 100;

    // Batch retrieve data for all controls
    try {
      const channelIds = validControls.map(({ control }) => control.id);

      ctx.dispatch(new GetBatchChannelData(channelIds, {}, { replaceDate: _hasZoomLevel })).subscribe({
        next: () => {
          try {
            if (batchWasEmpty && batchTestPhasesVisible) {
              firstValueFrom(ctx.dispatch(new LoadTestPhases()))
                .then(() => {
                  ctx.dispatch(GenerateEChartConfig);
                })
                .catch(() => {
                  ctx.dispatch(GenerateEChartConfig);
                });
            } else {
              ctx.dispatch(GenerateEChartConfig);
            }
          } catch (err) {
            console.warn('Failed to dispatch GenerateEChartConfig, context may have been destroyed', err);
          }
        },
        error: (err) => console.error('Error in GetBatchChannelData dispatch', err),
      });
    } catch (err) {
      console.warn('Failed to dispatch GetBatchChannelData, context may have been destroyed', err);
    }
  }

  /**
   * Update an existing control
   * @param ctx state context
   * @param param1 action parameters
   */
  export async function updateControl(ctx: StateContext<MultiViewStateModel>, { control }: AddControlToCurrentView) {
    try {
      await db.controls.put(control);
    } catch (e) {
      console.error('Error saving control to IndexedDB', e);
    }
    ctx.setState(
      produce(ctx.getState(), (draft) => {
        const controlSenorTableIndex = draft.views[getViewIndex(ctx)].sensorTable.findIndex(findbyId(control as StoreControl));
        if (controlSenorTableIndex === undefined) {
          return;
        }

        draft.views[getViewIndex(ctx)].lanes.forEach((l) => {
          l.controls = l.controls.map((c) => {
            if (c.id === control.id) {
              return { ...c, ...control };
            }
            return c;
          });
        });

        draft.views[getViewIndex(ctx)].sensorTable[controlSenorTableIndex] = {
          ...draft.views[getViewIndex(ctx)].sensorTable[controlSenorTableIndex],
          ...control,
        };
      })
    );
  }
}
